import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchData, HTTP_STATUS, intl } from 'dmsbasereact';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
    Button,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InboxIcon from '@mui/icons-material/Inbox';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { formatDateTime } from '../../utils/formatters';
import { button3DSx } from '../../constants/ButtonStyles';
import { CheckinDetailModal } from './CheckinDetailModal';

export const CheckinTable = ({ onCorrectInvoice, onManualEntry }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchData('/batch_location/checkin/invoices', 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setInvoices(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch check-in invoices', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const getTotalQuantity = (items) => {
        if (!items || items.length === 0) return 0;
        return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    };

    const handleViewDetails = (invoice) => {
        setSelectedInvoice(invoice);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedInvoice(null);
    };

    const handleCheckinConfirmed = () => {
        setModalOpen(false);
        setSelectedInvoice(null);
        fetchInvoices();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (invoices.length === 0) {
        return (
            <Box>
                {onManualEntry && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<NoteAddOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                            onClick={onManualEntry}
                            sx={button3DSx}
                        >
                            {intl.get('Checkin.ManualTab').d('Manual Entry')}
                        </Button>
                    </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 1 }}>
                    <InboxIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography variant="body1" color="text.secondary">
                        {intl.get("Checkin.NoPendingInvoices").d("No pending invoices for check-in")}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            {onManualEntry && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<NoteAddOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                        onClick={onManualEntry}
                        sx={button3DSx}
                    >
                        {intl.get('Checkin.ManualTab').d('Manual Entry')}
                    </Button>
                </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {intl.get("InvoiceNumber").d("Invoice Number")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {intl.get("Origin").d("Origin")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {intl.get("Checkin.SentDateTime").d("Sent Date/Time")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                {intl.get("Checkin.TotalQuantity").d("Total Quantity")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                {intl.get("Actions").d("Actions")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoices.map((invoice) => (
                            <TableRow key={invoice.pk} hover>
                                <TableCell>
                                    {invoice.danfe_number || '-'}
                                </TableCell>
                                <TableCell>
                                    {invoice.owner_entity_name || '-'}
                                </TableCell>
                                <TableCell>
                                    {formatDateTime(invoice.sent_at)}
                                </TableCell>
                                <TableCell align="right">
                                    {getTotalQuantity(invoice.items).toLocaleString('pt-BR')}
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title={intl.get("Checkin.ViewDetails").d("View Details")}>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleViewDetails(invoice)}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <CheckinDetailModal
                open={modalOpen}
                onClose={handleModalClose}
                invoice={selectedInvoice}
                onCheckinConfirmed={handleCheckinConfirmed}
                onCorrectInvoice={onCorrectInvoice}
            />
        </Box>
    );
};

CheckinTable.propTypes = {
    onCorrectInvoice: PropTypes.func,
    onManualEntry: PropTypes.func,
};
